/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import * as Styles from '@components/modals/styles';
import { ILooseObject } from '@interface/loose-object';
import { transfer } from '@lib/beacon';
import { RootState } from '@state/store';
import { validateAddress } from '@taquito/utils';
import { testNodeHealth } from '@utils/node';
import { removeDuplicates } from '@utils/validation';
import { AlertDialog, Box, Field, Input, RadioGroup, Textarea, TextNumberPair } from '@versumstudios/ui';

enum TRANSFER_MODE {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

interface IWalletPair {
  wallet: string;
  quantity: number;
}

export interface ITokenTransfer {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  platformName?: string;
  tokenKT?: string;
  tokenId?: number;
  tokensOwned?: number;
}

export const TokenTransfer: FC<ITokenTransfer> = ({
  open,
  onOpenChange,
  platformName,
  tokenKT,
  tokenId,
  tokensOwned,
}) => {
  const { account } = useSelector((state: RootState) => state.auth);
  const [transferMode, setTransferMode] = useState(TRANSFER_MODE.SINGLE);
  const [values, setValues] = useState<ILooseObject>({
    // single user
    wallet: { error: false, data: null },
    quantity: { error: false, data: tokensOwned && tokensOwned > 1 ? null : tokensOwned },
    // multiple users
    wallets: {
      error: false,
      data: '',
    },
    processed: { error: false, data: [] },
  });

  const handleChange = (key: string, value: any) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: false, data: value },
    }));
  };

  const handleProcessedChange = (index: number, wallet: string, quantity: number) => {
    const copy = [...values.processed.data];
    if (copy[index]) {
      copy[index].wallet = wallet;
      copy[index].quantity = Number(quantity);
    }
    handleChange('processed', copy);
  };

  const setError = (key: string, message: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: message, data: prevValues[key].data },
    }));
  };

  const validate = () => {
    let valid = true;

    if (transferMode === TRANSFER_MODE.SINGLE) {
      if (!values.quantity.data) {
        // validate single user transaction
        setError('quantity', '"Quantity" is missing');
        valid = false;
      }

      if (Math.round(values.quantity.data) > Number(tokensOwned)) {
        setError('quantity', `You only own ${tokensOwned} tokens`);
        valid = false;
      }

      if (Math.round(values.quantity.data) < 1) {
        setError('quantity', 'Minimum quantity 1 tokens');
        valid = false;
      }

      if (!values.wallet.data) {
        setError('wallet', '"Wallet" is missing');
        valid = false;
      }

      // if (values.wallet.data.indexOf('.tez') !== -1) {
      //   // '.tez wallet found. getting address');
      //   const address = await getAddressFromDomain(values.wallet.data);
      //   if (address !== values.wallet.data) {
      //     // ('received address is different that whats on the state. Update state to', address);
      //     if (validateAddress(address) !== 3) {
      //       setError('recipient', 'Address is invalid.');
      //       valid = false;
      //     } else {
      //       handleChange('recipient', address);
      //       valid = true;
      //     }
      //   }
      // }

      if (validateAddress(values.wallet.data) !== 3) {
        setError('wallet', '"Address" is invalid.');
        valid = false;
      }
    }

    if (transferMode === TRANSFER_MODE.MULTIPLE) {
      if (!values.wallets.data) {
        setError('wallets', '"Wallets" is missing');
        valid = false;
      }
    }

    const sum = values.processed.data.reduce((acc: number, { quantity }: IWalletPair) => acc + quantity, 0);

    if (tokensOwned && sum > tokensOwned) {
      setError('processed', `You only own ${tokensOwned} tokens`);
      valid = false;
    }

    return valid;
  };

  const handleConfirm = async () => {
    if (validate() && account && platformName && tokenId) {
      await testNodeHealth();
      // single
      if (transferMode === TRANSFER_MODE.SINGLE) {
        transfer(
          account.address,
          [{ wallet: values.wallet.data, quantity: Number(values.quantity.data) }],
          platformName,
          tokenKT!,
          tokenId
        );
      }

      // multiple
      if (transferMode === TRANSFER_MODE.MULTIPLE) {
        transfer(account.address, values.processed.data, platformName, tokenKT!, tokenId);
      }

      if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  const handleCancel = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  // receives an array of wallets and manually adds them
  const handleProcess = (wallets: string[]) => {
    handleChange(
      'processed',
      removeDuplicates(wallets).map((wallet) => {
        return { wallet, quantity: 1 };
      })
    );
  };

  useEffect(() => {
    handleProcess(values.wallets.data.split('\n'));
  }, [values.wallets.data]);

  return (
    <AlertDialog
      title="Transfer Token"
      description={`You own ${tokensOwned} editions of this token.`}
      open={open}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    >
      <Styles.Content>
        {tokensOwned && tokensOwned > 1 && (
          <RadioGroup
            value={transferMode}
            onChange={(value: string) => setTransferMode(value as TRANSFER_MODE)}
            data={[
              {
                key: TRANSFER_MODE.SINGLE,
                label: 'Single User',
              },
              {
                key: TRANSFER_MODE.MULTIPLE,
                label: 'Multiple Users',
              },
            ]}
          />
        )}

        {(transferMode === TRANSFER_MODE.SINGLE || (tokensOwned && tokensOwned === 1)) && (
          <>
            {tokensOwned && tokensOwned > 1 && (
              <Field uid="quantity" label="Quantity" error={values.quantity.error}>
                <Input
                  type="number"
                  placeholder="e.g.: 1"
                  min={1}
                  max={tokensOwned}
                  value={values.quantity.data || ''}
                  onChange={(value: string) => handleChange('quantity', value)}
                />
              </Field>
            )}
            <Field uid="wallet" label="Wallet" error={values.wallet.error}>
              <Input
                type="text"
                placeholder="e.g.: tz1keax..."
                onChange={(value: string) => handleChange('wallet', value)}
              />
            </Field>
          </>
        )}

        {transferMode === TRANSFER_MODE.MULTIPLE && tokensOwned && tokensOwned > 1 && (
          <>
            <Field uid="wallets" label="Wallets" tooltip="One wallet per line" error={values.wallets.error}>
              <Textarea
                placeholder="e.g.: tz1keax..."
                value={values.wallets.data}
                onChange={(value: string) => handleChange('wallets', value)}
              />
            </Field>

            {values.processed.data.length > 0 && (
              <Field uid="processed" label="Processed Wallets" error={values.processed.error}>
                {values.processed.data.map((pair: IWalletPair, index: number) => {
                  return (
                    <Box key={pair.wallet} css={{ marginBottom: '$space$4' }}>
                      <TextNumberPair
                        address={pair.wallet}
                        value={pair.quantity}
                        editable={false}
                        onChange={(value: string, percentage: number) =>
                          handleProcessedChange(index, value, percentage)
                        }
                      />
                    </Box>
                  );
                })}
              </Field>
            )}
          </>
        )}
      </Styles.Content>
    </AlertDialog>
  );
};
